import React, { useEffect, useState } from "react";
import { isCrossBranchAdmin } from "../../utils/branchAccess";

const canPickBranches = (role, extraRoles = []) =>
  isCrossBranchAdmin(role) || extraRoles.map(String).includes(String(role ?? ""));

export const GYM_BRANCHES = [
  { id: "15", name: "F-11" },
  { id: "1", name: "G-13" },
];

/** Initial value for dashboard branch state. */
export function initialDashboardBranch(role, loginBranchId, { allowAll = true, extraRoles = [] } = {}) {
  if (canPickBranches(role, extraRoles)) {
    if (allowAll) return "all";
    const login = String(loginBranchId ?? "");
    if (GYM_BRANCHES.some((b) => b.id === login)) return login;
    return "15";
  }
  return loginBranchId != null && loginBranchId !== "" ? String(loginBranchId) : "";
}

/** Effective branch for API calls. Super Admin, and any extraRoles, may use the selection. */
export function resolveDashboardBranch(role, loginBranchId, selected, extraRoles = []) {
  if (canPickBranches(role, extraRoles)) {
    return selected === "all" || selected === "" || selected == null
      ? "all"
      : String(selected);
  }
  return loginBranchId != null && loginBranchId !== "" ? String(loginBranchId) : "";
}

/** Pass to APIs that treat missing branch_id as all branches. */
export function apiBranchParam(branch) {
  if (!branch || branch === "all") return undefined;
  return branch;
}

/**
 * Super Admin can pick All / F-11 / G-13.
 * Pass extraRoles to allow the same picker on one page.
 * Other roles see their login branch and the dropdown is disabled.
 */
export default function DashboardBranchFilter({
  role,
  loginBranchId,
  value,
  onChange,
  allowAll = true,
  extraRoles = [],
  className = "form-select",
  id,
  style,
}) {
  const canPick = canPickBranches(role, extraRoles);
  const effective = canPick
    ? value ?? (allowAll ? "all" : "")
    : String(loginBranchId ?? "");

  const choices = GYM_BRANCHES.filter((b) =>
    canPick ? true : b.id === String(loginBranchId ?? "")
  );

  return (
    <select
      id={id}
      className={className}
      style={style}
      value={effective}
      disabled={!canPick}
      aria-label="Branch"
      onChange={(e) => {
        if (!canPick) return;
        onChange?.(e.target.value);
      }}
    >
      {allowAll && canPick ? <option value="all">All Branches</option> : null}
      {(choices.length ? choices : GYM_BRANCHES).map((branch) => (
        <option key={branch.id} value={branch.id}>
          {branch.name}
        </option>
      ))}
      {!canPick &&
      loginBranchId != null &&
      loginBranchId !== "" &&
      !GYM_BRANCHES.some((b) => b.id === String(loginBranchId)) ? (
        <option value={String(loginBranchId)}>Branch {loginBranchId}</option>
      ) : null}
    </select>
  );
}

/**
 * Convenience hook: branch state that stays locked to login for non–Super Admin.
 */
export function useDashboardBranch(role, loginBranchId, options = {}) {
  const { allowAll = true, extraRoles = [] } = options;
  const extraKey = extraRoles.map(String).join(",");
  const canPick = canPickBranches(role, extraRoles);
  const [branch, setBranch] = useState(() =>
    initialDashboardBranch(role, loginBranchId, { allowAll, extraRoles })
  );

  useEffect(() => {
    if (!canPick) {
      setBranch(initialDashboardBranch(role, loginBranchId, { allowAll, extraRoles }));
    }
  }, [canPick, role, loginBranchId, allowAll, extraKey]);

  const effective = resolveDashboardBranch(role, loginBranchId, branch, extraRoles);
  const apiBranch = apiBranchParam(effective);

  return {
    branch: effective,
    setBranch: canPick ? setBranch : () => {},
    canPick,
    apiBranch,
    /** For APIs that need a concrete branch id (no "all"). */
    requiredBranch:
      effective === "all"
        ? String(loginBranchId || "15")
        : effective || String(loginBranchId || "15"),
  };
}
